/**
 * UI preferences store — leaf module with no api/socket/store barrel imports.
 * ThemeContext and useTranslation import from here so public pages do not
 * evaluate the full store monolith for theme/i18n.
 */
import { create } from 'zustand';
import { Platform } from 'react-native';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  DEFAULT_UI_STATE,
  clampContextSidebarWidth,
  resolveContextSidebarWidth,
  isArtifactNavigationActive,
  clampSidebarWidth,
  ARTIFACT_LAYOUT_VERSION,
  isArtifactSidebarType,
} from '@/constants/config';
import { getStorage } from './storage';
import { mergeSidebarChatOrder } from '@/lib/sidebarChatOrder';
import { normalizeReadAloudVoice } from '@/constants/readAloudVoices';

const currentContextSidebarWidths = (widths, version) => Object.fromEntries(
  Object.entries(widths || {}).filter(([type]) => version === ARTIFACT_LAYOUT_VERSION || !isArtifactSidebarType(type)),
);

export const useUIStore = create(
  persist(
    (set) => ({
      ...DEFAULT_UI_STATE,
      macSidebarCompact: false,
      macSidebarOverlayOpen: false,
      macSidebarInitialized: false,
      initializeMacSidebar: compact => set(state => state.macSidebarInitialized ? state : {
        macSidebarInitialized: true,
        isSidebarVisible: true,
        macSidebarOverlayOpen: compact,
      }),
      setMacSidebarCompact: compact => set(state => state.macSidebarCompact === compact
        ? state : { macSidebarCompact: compact, macSidebarOverlayOpen: false }),
      closeMacSidebarOverlay: () => set({ macSidebarOverlayOpen: false }),
      sidebarBotOrderByUser: {},
      setSidebarBotOrder: (userId, ids) => set(state => ({
        sidebarBotOrderByUser: { ...state.sidebarBotOrderByUser, [userId]: [...new Set(ids)] },
      })),
      sidebarChatOrderByUser: {},
      setSidebarChatOrder: (userId, scope, ids) => set(state => {
        if (!userId || !scope) return state;
        const userOrders = state.sidebarChatOrderByUser[userId] || {};
        return { sidebarChatOrderByUser: { ...state.sidebarChatOrderByUser, [userId]: {
          ...userOrders, [scope]: ids.length ? mergeSidebarChatOrder(userOrders[scope], ids) : [],
        } } };
      }),

      toggleSection: (sectionId) => set((state) => ({
        collapsedSections: {
          ...state.collapsedSections,
          [sectionId]: !state.collapsedSections[sectionId],
        },
      })),
      setMessageLayout: (layout) => set({ messageLayout: layout }),
      setMessageWidth: (width) => set({ messageWidth: width }),
      setTagsLayout: (layout) => set({ tagsLayout: layout }),
      setSidebarWidth: (width) => set({ sidebarWidth: clampSidebarWidth(width) }),
      setIncreaseBorderContrast: (increase) => set({ increaseBorderContrast: increase }),
      setShowTagCounts: (show) => set({ showTagCounts: show }),
      setShowTimestamps: (show) => set({ showTimestamps: show }),
      setShowMessageActions: (show) => set({ showMessageActions: show }),
      setShowSourcesOutputsPanel: (show) => set({ showSourcesOutputsPanel: show }),
      setChatInputStyle: (style) => set({ chatInputStyle: style }),
      setEnterToSend: (value) => set({ enterToSend: value === true }),
      setWorkNotificationSounds: (value) => set({ workNotificationSounds: value === true }),
      setReadAloudVoice: (value) => set({ readAloudVoice: normalizeReadAloudVoice(value) }),
      setWorkNotifications: (value) => set({ workNotifications: value === true }),
      setKeepScreenAwakeInChats: (value) => set({ keepScreenAwakeInChats: value === true }),
      setWebSearchEnabled: (enabled) => set({ webSearchEnabled: Boolean(enabled) }),
      setTemporaryChatUsePersonalization: (enabled) => set({
        temporaryChatUsePersonalization: enabled === true,
      }),
      setLanguage: (language) => set({ language: language }),
      toggleSidebar: () => set((state) => Platform.OS === 'macos' && state.macSidebarCompact
        ? { macSidebarOverlayOpen: !state.macSidebarOverlayOpen }
        : isArtifactNavigationActive(state)
        ? { artifactNavigationExpanded: !state.artifactNavigationExpanded }
        : { isSidebarVisible: !state.isSidebarVisible }),
      setSidebarVisible: (isVisible) => set(state => Platform.OS === 'macos' && state.macSidebarCompact
        ? { macSidebarOverlayOpen: isVisible }
        : { isSidebarVisible: isVisible }),
      // Opens at the width this type was last resized to, or its own default —
      // never at whatever width the previously opened type happened to use.
      openContextSidebar: (type, props) => set((state) => {
        const widths = currentContextSidebarWidths(state.contextSidebarWidths, state.contextSidebarLayoutVersion);
        const remembered = widths[type];
        const width = Number.isFinite(remembered)
          ? clampContextSidebarWidth(type, remembered)
          : resolveContextSidebarWidth(type, typeof window === 'undefined' ? 0 : window.innerWidth);
        return {
          isContextSidebarVisible: true,
          contextSidebarType: type,
          contextSidebarProps: props || null,
          contextSidebarWidth: width,
          contextSidebarWidths: widths,
          contextSidebarWidthMode: Number.isFinite(remembered) ? 'custom' : 'auto',
          contextSidebarLayoutVersion: ARTIFACT_LAYOUT_VERSION,
          artifactNavigationExpanded: false,
        };
      }),
      closeContextSidebar: () => set({
        isContextSidebarVisible: false,
        contextSidebarType: null,
        contextSidebarProps: null,
        contextSidebarWidthMode: 'auto',
        artifactNavigationExpanded: false,
      }),
      setVideoTimelineOrder: (taskId, order) => set((state) => ({
        videoTimelineOrders: { ...(state.videoTimelineOrders || {}), [taskId]: order },
      })),
      setVideoProjectContext: (ctx) => set({ videoProjectContext: ctx || null }),
      setVideoTakePick: (key, pick) => set((state) => {
        if (!key) return state;
        if (!pick) {
          const next = { ...(state.videoTakePicks || {}) };
          delete next[key];
          return { videoTakePicks: next };
        }
        return { videoTakePicks: { ...(state.videoTakePicks || {}), [key]: pick } };
      }),
      clearVideoTakePicks: () => set({ videoTakePicks: {} }),
      setContextSidebarProps: (props) => set({ contextSidebarProps: props || null }),
      setContextSidebarWidth: (width, type) => set((state) => {
        const key = type || state.contextSidebarType;
        if (!key) return { contextSidebarWidth: width };
        return {
          contextSidebarWidth: width,
          contextSidebarWidthMode: 'custom',
          contextSidebarLayoutVersion: ARTIFACT_LAYOUT_VERSION,
          contextSidebarWidths: {
            ...currentContextSidebarWidths(state.contextSidebarWidths, state.contextSidebarLayoutVersion),
            [key]: width,
          },
        };
      }),
      setChatInputDetailedWidth: (width) => set({ chatInputDetailedWidth: width }),

      requestScrollToMessage: ({
        conversationId,
        messageId,
        viewPosition,
        searchQuery = null,
        isArchivedMessage = false,
        archiveOriginalIndex = null,
        animated = true,
        pinAnchor = false,
      }) => set({
        pendingScrollRequest: {
          conversationId,
          messageId,
          viewPosition,
          searchQuery,
          isArchivedMessage,
          archiveOriginalIndex,
          animated,
          pinAnchor,
          requestedAt: Date.now(),
        },
      }),
      clearPendingScrollRequest: () => set({ pendingScrollRequest: null }),

      highlightedMessageId: null,
      highlightedSearchQuery: null,
      highlightedMessagePersist: false,
      pendingConversationFindRequest: null,
      setHighlightedMessageId: (messageId, searchQuery, options = {}) => set({
        highlightedMessageId: messageId,
        highlightedSearchQuery: searchQuery || null,
        highlightedMessagePersist: options?.persist === true,
      }),
      clearHighlightedMessageId: () => set({
        highlightedMessageId: null,
        highlightedSearchQuery: null,
        highlightedMessagePersist: false,
      }),

      requestOpenConversationFind: (conversationId, query = '') => set({
        pendingConversationFindRequest: {
          conversationId,
          query: typeof query === 'string' ? query : '',
          requestedAt: Date.now(),
        },
      }),
      clearPendingConversationFindRequest: () => set({ pendingConversationFindRequest: null }),

      requestScrollToTop: (conversationId) => set({
        pendingScrollToTopRequest: { conversationId, requestedAt: Date.now() },
      }),
      clearPendingScrollToTopRequest: () => set({ pendingScrollToTopRequest: null }),

      setHeroGradientOpacity: (opacity) => set({ heroGradientOpacity: opacity }),

      incrementFreeTrialPromptCount: () => set((state) => ({
        freeTrialPromptCount: (state.freeTrialPromptCount || 0) + 1,
      })),

      clearUI: () => {
        set({
          ...DEFAULT_UI_STATE,
          macSidebarOverlayOpen: false,
          pendingScrollRequest: null,
          pendingScrollToTopRequest: null,
          pendingConversationFindRequest: null,
          highlightedMessageId: null,
          highlightedSearchQuery: null,
          highlightedMessagePersist: false,
        });
      },
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => getStorage()),
      partialize: ({ macSidebarCompact, macSidebarOverlayOpen, ...preferences }) => preferences,
      merge: (persisted, current) => {
        const persistedObj = persisted && typeof persisted === 'object' ? persisted : {};
        return {
          ...current,
          ...persistedObj,
          macSidebarCompact: current.macSidebarCompact,
          macSidebarOverlayOpen: false,
          sidebarWidth: clampSidebarWidth(persistedObj.sidebarWidth ?? current.sidebarWidth),
          readAloudVoice: normalizeReadAloudVoice(persistedObj.readAloudVoice ?? current.readAloudVoice),
          // One-shot requests and the search highlight are per-session. They are
          // written to storage with the preferences, and a
          // rehydrated `highlightedMessagePersist` never expires on its own —
          // it would pin a stale highlight on a message across reloads.
          pendingScrollRequest: null,
          pendingScrollToTopRequest: null,
          pendingConversationFindRequest: null,
          highlightedMessageId: null,
          highlightedSearchQuery: null,
          highlightedMessagePersist: false,
          // Editors are conversation-scoped. Rehydrating an open
          // document-editor onto /app or a different /c/:id shows the
          // previous thread's Word file in the wrong place.
          isContextSidebarVisible: false,
          contextSidebarType: null,
          contextSidebarProps: null,
          contextSidebarWidthMode: 'auto',
          contextSidebarLayoutVersion: ARTIFACT_LAYOUT_VERSION,
          artifactNavigationExpanded: false,
          videoProjectContext: null,
          videoTakePicks: {},
          // Keep an explicit boolean so `false` is not dropped on rehydrate.
          enterToSend: typeof persistedObj.enterToSend === 'boolean'
            ? persistedObj.enterToSend
            : current.enterToSend,
          // The former keepScreenAwake key was automatically persisted as true.
          // Only an explicit opt-in to the chat-scoped preference enables it.
          keepScreenAwakeInChats: persistedObj.keepScreenAwakeInChats === true,
          temporaryChatUsePersonalization: typeof persistedObj.temporaryChatUsePersonalization === 'boolean'
            ? persistedObj.temporaryChatUsePersonalization
            : current.temporaryChatUsePersonalization,
          collapsedSections: {
            ...(current?.collapsedSections || {}),
            ...((persistedObj.collapsedSections && typeof persistedObj.collapsedSections === 'object')
              ? persistedObj.collapsedSections
              : {}),
          },
          contextSidebarWidths: currentContextSidebarWidths({
            ...(current?.contextSidebarWidths || {}),
            ...((persistedObj.contextSidebarWidths && typeof persistedObj.contextSidebarWidths === 'object')
              ? persistedObj.contextSidebarWidths
              : {}),
          }, persistedObj.contextSidebarLayoutVersion),
        };
      },
    },
  ),
);

/** Apply a logged-in user's saved "Press Enter to send" choice. Only `false`
 *  is written so the default (Enter sends) stays intact for everyone else.
 *  Wait for ui-storage hydration so a stale local `true` cannot overwrite it. */
export function applyServerEnterToSend(serverEnterToSend) {
  if (serverEnterToSend !== false) return;

  const apply = () => useUIStore.getState().setEnterToSend(false);
  const persistApi = useUIStore.persist;
  if (!persistApi || typeof persistApi.hasHydrated !== 'function' || persistApi.hasHydrated()) {
    apply();
    return;
  }
  const unsub = persistApi.onFinishHydration?.(() => {
    unsub?.();
    apply();
  });
}

export default useUIStore;
